import { NativePresentationMode } from '../mobileSettings';
import type { ActionMessage, InvokeActionFnReturnType } from '../types';
import { invokeAction } from './invoke';
import { LimeProductLine } from './redirectToHome';
import type { Actions } from './types';

interface BaseOpenChatProps {
  readonly replaceScreen?: boolean;
  /**
   * wrap the action in a goToRoute action with the given product line
   */
  readonly fromLimePage?: LimeProductLine | true;
  /**
   * wrap the action in a dismiss action if true, defaults to true if mobile presentation mode is root
   */
  readonly dismiss?: boolean;
}

interface OpenChatV1Props {
  readonly route: string;
  readonly routeParams?: Record<string, unknown>;

  // hide back button in chat (only on android)
  readonly hideBack?: boolean;
}

interface OpenChatV2Props {
  readonly subdomain: string;
  readonly scriptIdentifier: string;
  readonly useNewChat?: boolean;
  readonly fetchHistory?: boolean;
  readonly params?: Record<string, unknown>;
}

type OpenChatProps = OpenChatV1Props | OpenChatV2Props;
type OpenChatActionProps = BaseOpenChatProps &
  (
    | OpenChatV1Props
    | {
        readonly answerUrlRequestData: OpenChatV2Props;
      }
  );

export function isOpenChatV2(props: OpenChatProps): props is OpenChatV2Props {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return (props as OpenChatV2Props).subdomain !== undefined;
}

// open chat action is called 'goToRoute' in the app
export type OpenChatAction = ActionMessage<'goToRoute', OpenChatActionProps>;
export async function openChat({
  replaceScreen = false,
  fromLimePage: fromPage,
  dismiss = window.mobileSettings?.presentationMode === NativePresentationMode.Root,
  ...data
}: BaseOpenChatProps & OpenChatProps): InvokeActionFnReturnType<Actions, 'goToRoute' | 'redirectToHome'> {
  const actionData = isOpenChatV2(data)
    ? {
        replaceScreen,
        answerUrlRequestData: data,
      }
    : {
        hideBack: (data as OpenChatV1Props).hideBack ?? false,
        replaceScreen,
        ...data,
      };

  if (fromPage == null) {
    // eslint-disable-next-line @typescript-eslint/return-await
    return invokeAction('appAction', 'goToRoute')(actionData);
  }

  const goToRouteData = {
    initialPage: typeof fromPage === 'boolean' ? LimeProductLine.Renters : fromPage,
    action: { type: 'goToRoute', data: actionData },
  } as const;

  if (dismiss) {
    // eslint-disable-next-line @typescript-eslint/return-await
    return invokeAction(
      'uniclientAction',
      'dismiss',
    )({
      type: 'redirectToHome',
      data: goToRouteData,
    });
  }

  // eslint-disable-next-line @typescript-eslint/return-await
  return invokeAction('appAction', 'redirectToHome')(goToRouteData);
}
